<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType } = useMessageContext();
const { t } = useI18n();
const { isAWebWidgetInbox } = useInbox();

const inputSelectOptions = computed(() => {
  if (contentType.value !== CONTENT_TYPES.INPUT_SELECT) {
    return [];
  }

  return Array.isArray(contentAttributes.value?.items)
    ? contentAttributes.value.items
    : [];
});

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});

const selectedInputSelectValue = computed(() => {
  if (contentType.value !== CONTENT_TYPES.INPUT_SELECT) {
    return '';
  }

  return contentAttributes.value?.submittedValues?.[0]?.value || '';
});

const showInputSelectOptions = computed(() => {
  return (
    contentType.value === CONTENT_TYPES.INPUT_SELECT &&
    inputSelectOptions.value.length > 0
  );
});

const inputSelectOptionClass = option => {
  const isSelected = selectedInputSelectValue.value === option.value;

  return isSelected
    ? 'border-n-brand bg-n-brand/10 text-n-blue-11'
    : 'border-n-weak bg-n-alpha-1 text-n-slate-11';
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="csat">
    <span v-dompurify-html="content" :title="content" />
    <div v-if="showInputSelectOptions" class="mt-4 flex flex-wrap gap-2">
      <div
        v-for="(option, index) in inputSelectOptions"
        :key="`${option.title || option.value}-${index}`"
        class="inline-flex min-h-9 items-center rounded-full border px-3 py-1.5 text-sm font-medium"
        :class="inputSelectOptionClass(option)"
      >
        {{ option.title || option.value }}
      </div>
    </div>
    <dl v-if="formValues.length" class="mt-4">
      <template v-for="item in formValues" :key="item.title">
        <dt class="text-n-slate-11 italic mt-2">
          {{ item.label || t('CONVERSATION.RESPONSE') }}
        </dt>
        <dd>{{ item.title }}</dd>
      </template>
    </dl>
    <div v-else-if="isAWebWidgetInbox" class="my-2 font-medium">
      {{ t('CONVERSATION.NO_RESPONSE') }}
    </div>
  </BaseBubble>
</template>
